// src/pages/Encounter.jsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faRotate, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { useAppointment } from '../contexts/AppointmentContext';
import { useUser } from '../contexts/UserContext';
import Spinner from '../components/Spinner';
import IframeComponent from '../components/IframeComponent';

// Define a threshold for small devices (e.g., typical tablet portrait width)
const SMALL_DEVICE_WIDTH_THRESHOLD = 768;

const Encounter = () => {
    const apptContext  = useAppointment();
    const { user, loadingSession } = useUser();

    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [confInfo, setConfInfo] = useState(null);
    const [error, setError] = useState('');
    // Initialize isPortrait based on initial screen width check
    const [isPortrait, setIsPortrait] = useState(window.innerWidth < SMALL_DEVICE_WIDTH_THRESHOLD);
    const [isFullSize, setIsFullSize] = useState(false);
    const [iframeContainerHeight, setIframeContainerHeight] = useState(0);
    const [iframeContainerWidth, setIframeContainerWidth] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0); // Height of the main app header
    const [currentTime, setCurrentTime] = useState(new Date());

    const navigate = useNavigate();
    const videoAreaRef = useRef(null);

    // updateDimensions function
    const updateDimensions = useCallback(() => {
	        let currentHeaderHeight = 0;
        const actualHeaderElement = document.getElementById('header'); // Main app header
        if (actualHeaderElement) {
            currentHeaderHeight = actualHeaderElement.offsetHeight;
            setHeaderHeight(prev => prev !== currentHeaderHeight ? currentHeaderHeight : prev);
	        } else {
            setHeaderHeight(0);
        }

        if (isFullSize) {
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
	            setIframeContainerHeight(viewportHeight - currentHeaderHeight);
	            setIframeContainerWidth(viewportWidth);
	        } else if (videoAreaRef.current) {
            const containerElement = videoAreaRef.current;
            const contentHeight = containerElement.offsetHeight;
            const contentWidth = containerElement.offsetWidth;
	            if (contentHeight > 0 && contentWidth > 0) {
	                setIframeContainerHeight(contentHeight);
	                setIframeContainerWidth(contentWidth);
	            }
	        }
    }, [isFullSize]);

    // Use useEffect for measurements on mount and resize
    useEffect(() => {
        const handleResize = () => {
             // Also update portrait state on resize if needed
             setIsPortrait(window.innerWidth < SMALL_DEVICE_WIDTH_THRESHOLD);
             requestAnimationFrame(updateDimensions);
        };
        const animationFrameId = requestAnimationFrame(updateDimensions);
        window.addEventListener('resize', handleResize);
        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
        }
    }, [updateDimensions]); // updateDimensions already depends on isFullSize

    // Effect for current time
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Ref to ensure we only ever join once per session
    const hasJoinedRef = useRef(false);

    // make sure it gets cleaned up
    useEffect(() => {
        return () => {
            hasJoinedRef.current = false;
        };
    }, []);

    // Effect for fetching conference info
    useEffect(() => {
        const appt = apptContext.currentAppointment;

        if (
            hasJoinedRef.current  ||
            loadingSession        ||
            !appt?.appointment_id ||
            !user?.userid && !confInfo
        ) { return; }

        let isMounted = true;
        hasJoinedRef.current = true; // mark “in flight” so we don’t run again

        (async () => {
        setIsVideoLoading(true);
        setError('');

        try {
            if (!isMounted) return;
            const confData = await apptContext.joinAppointment(appt.appointment_id);            

            if (confData.success && confData.opentokurl) {
                setConfInfo({ opentokurl: confData.opentokurl });
            } else {
                const msg = confData.error || 'Unknown error joining conference';
                toast.error(msg, { duration: 5000 });
                setError(`Error joining conference: ${msg}`);
                setConfInfo(null);
            }
	        } catch {
	            if (!isMounted) return;
	            toast.error('Network error. Please try again.', { duration: 5000 });
            setError('A network error occurred while joining the conference.');
            setConfInfo(null);
        } finally {
            if (isMounted) setIsVideoLoading(false);
        }
        })();

        return () => { isMounted = false; };
    }, [
        apptContext.currentAppointment?.appointment_id,
        loadingSession,
    ]);


    const formatDateTime = () => {
        const isSmallScreen = window.innerWidth < SMALL_DEVICE_WIDTH_THRESHOLD; // Use threshold
        return format(currentTime, isSmallScreen ? "P h:mmaaaaa" : "PP pp");
    };

    const toggleFullSize = () => setIsFullSize(prev => !prev);

    // Define the content to be rendered (iframe + controls passed as children)
    const videoConferenceContent = !isVideoLoading && !error && confInfo?.opentokurl ? (
        <div
            className="w-full h-full flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: isFullSize ? 'black' : 'transparent' }}
        >
            <IframeComponent
                opentokUrl={confInfo.opentokurl}
                isPortrait={isPortrait} // Pass the state down
                containerHeight={iframeContainerHeight > 0 ? iframeContainerHeight : 1}
                containerWidth={iframeContainerWidth > 0 ? iframeContainerWidth : 1}
            >
                {/* Controls Container */}
                <div className="absolute inset-0 pointer-events-none z-10">
                     <button
                        onClick={toggleFullSize}
                        className="absolute top-2 right-2 p-2 bg-none bg-opacity-30 text-white text-lg rounded-md hover:bg-opacity-50 pointer-events-auto"
                        aria-label={isFullSize ? 'Return to Normal Size' : 'Expand to Full Size'}
                        title={isFullSize ? 'Return to Normal Size' : 'Expand to Full Size'}
                    >
                        <FontAwesomeIcon icon={isFullSize ? faCompress : faExpand} />
                    </button>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="absolute bottom-1 left-4 w-12 h-12 bg-secondary text-white rounded-full border-2 border-white flex items-center justify-center hover:bg-gray-600 drop-shadow-md pointer-events-auto"
                        aria-label="Exit Encounter"
                        title="Exit Encounter"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                    </button>
                    <button
                        onClick={() => setIsPortrait(!isPortrait)}
                        className="absolute bottom-1 right-4 w-12 h-12 bg-secondary text-white rounded-full border-2 border-white flex items-center justify-center hover:bg-gray-600 drop-shadow-md pointer-events-auto"
                        aria-label={isPortrait ? 'Switch to Landscape' : 'Switch to Portrait'}
                        title={isPortrait ? 'Switch to Landscape' : 'Switch to Portrait'}                    >
                        <FontAwesomeIcon icon={faRotate} size="lg" />
                    </button>
                </div>
            </IframeComponent>
        </div>
    ) : null;

    // Use React Fragment as the root
    return (
        <>
            {/* Main container for the Encounter page content */}
            <div
                className="flex flex-col flex-grow w-full h-full"
            >
                {/* 1. Header Section (within Normal Mode) */}
                {!isFullSize && (
                    <div className="flex items-center justify-between p-4 border-b shrink-0 bg-neutral-lightest">
                        <h2 className="text-xl font-semibold text-primary text-left md:text-center flex-1 mr-4 truncate">
                            Telemedicine Conference
                        </h2>
                        <div className="text-neutral-dark text-sm md:text-base whitespace-nowrap">
                            {formatDateTime()}
                        </div>
                    </div>
                )}

                {/* 2. Video/Loading/Error Area */}
                <div
                    ref={videoAreaRef}
                    className={`
                        w-full overflow-hidden
                        ${isFullSize
                            ? 'fixed inset-0 z-20 bg-black' // Fixed position for full size
                            : 'relative flex-grow flex items-center justify-center' // Add relative ONLY for normal mode
                        }
                    `}
                    style={isFullSize ? { top: `${headerHeight}px` } : {}}
                >
                    {/* Loading State */}
                    {isVideoLoading && (
                        <div className={`flex flex-col items-center justify-center text-center p-4 h-full ${isFullSize ? 'text-white' : ''}`}>
                            <Spinner />
                            <p className={`mt-2 ${isFullSize ? 'text-neutral-light' : 'text-neutral-dark'}`}>Loading conference details...</p>
                        </div>
                    )}
                    {/* Error State */}
                    {!isVideoLoading && error && (
                        <div className={`flex flex-col items-center justify-center text-center p-4 h-full ${isFullSize ? 'text-red-300' : ''}`}>
                             {!isFullSize && (
                                <div className="border border-red-300 bg-red-50 rounded-md p-4 max-w-md mx-auto">
                                    <p className="text-red-600 font-semibold">Error</p>
                                    <p className="text-red-500 mt-1">{error}</p>
                                </div>
                             )}
                             {isFullSize && (
                                <>
                                    <p className="font-semibold">Error</p>
                                    <p className="mt-1">{error}</p>
                                </>
                             )}
                        </div>
                    )}
                    {/* Video Content */}
                    {videoConferenceContent}
                    {/* Placeholder */}
                    {!isVideoLoading && !error && !confInfo && (
                        <div className={`flex flex-col items-center justify-center text-center p-4 h-full ${isFullSize ? 'text-neutral-light' : 'text-neutral-dark'}`}>
                            <p>Could not load conference information.</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Encounter;
